import React, { useState, useEffect, useRef, useMemo } from "react"
import { useTheme } from '~/contexts/ThemeContext'
import { usePlp } from '~/contexts/PlpContext'
import { notBlank } from '~/utils/helpers'

import Tile from '~/components/tile/tile'
import Bar from '~/components/plp/bar'
import Filters from '~/components/plp/filters'
import Sorts from '~/components/plp/sorts'
import Hero from '~/components/plp/hero'
import GroupNav from '~/components/plp/groupNav'
import Subnav from '~/components/plp/subnav'
import CollGroup from '~/components/plp/collGroup'
import CollBlocks from '~/components/plp/collBlocks'
import SeoDescription from '~/components/plp/seoDescription'

import { aria_btn } from "~/styles/app.module.scss"
import * as styles from "~/styles/plp/plp.module.scss"
import * as gstyles from "~/styles/plp/plp_group.module.scss"


const Plp = () => {
  const { setBread, isMobile, collPanel, locate, storesData } = useTheme()
  const { 
    collection, plpHook, products,
    currGroup, flats, scale, 
    panelRef, panelClose, handleQuick,
    showGrpNav, ungroups
  } = usePlp()

  const { checkStoreGend } = plpHook
  const storeGendConflct = useMemo(() => {
    return checkStoreGend({ locate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locate])

  const flatProds = (prods) => {
    let prodsarr = prods.flat()
    if (prodsarr.find(x => x.hasOwnProperty('products'))) {
      prodsarr = prodsarr.flatMap(x => x.products)
    }

    return prodsarr.slice(0,12).map(x => x.mid)
  }
  const tileIndxIds = useMemo(() => {
    return flatProds(products)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { title, subnav } = collection
  const { hide_title } = collection.rules || {}

  const [didLoad, setDidLoad] = useState(false)
  const [tileCount, setTileCount] = useState(0)
  const [wasRedirect, setWasRedirect] = useState(false)

  const tilesRef = useRef(null)

  const has_hero = useMemo(() => 
    notBlank(collection.hero.hero) && notBlank(collection.hero.hero_mobile) ? collection.hero : false
  , [collection])

  const seodesc = useMemo(() => 
    notBlank(collection.description) ? collection.description : collection.seo_desc
  , [collection])

  const is_block = (obj) => {
    return obj.hasOwnProperty('blocks') && obj.blocks && obj.blocks.length > 0
  }

  const tileKey = (product, index) => {
    let tile_key = `tile${index}${product?.mid}`
    if (product?.handle) tile_key = `${product.handle}${index}`
    if (!product?.handle && product?.data) tile_key = `${product.mid}${product.data.name}`
    if (product?.group && product.group === 'div') tile_key = product.name
    return tile_key
  }

  const storeName = () => {
    const store = storesData.flatMap(x => x.stores).find(x => x.short_name === locate.currStores[0])
    return store?.title || 'selected store'
  }

  useEffect(() => {
    if (sessionStorage.getItem('wasRedirect')) {
      setWasRedirect(true)
      sessionStorage.removeItem('wasRedirect')
    }
    setDidLoad(true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])



  return (
    <div className="plp">
      {has_hero && (
        <Hero data={has_hero} title={title} />
      )}

      <div className={`${styles.head} ${hide_title && showGrpNav ? styles.nopadd : ''}`} plp-head=''>
        <h1 className={[styles.title, hide_title ? styles.hide : ''].join(' ')}>
          {title}
        </h1>
        {(didLoad && isMobile) && (
          <div className={styles.coll_count} aria-live="polite" aria-atomic="true" coll-count="" aria-relevant="text"></div>
        )}
        {wasRedirect && (
          <div className={styles.wasredir}>
            Sorry, we couldn't find that product, but check out the similar items in this collection.
          </div>
        )}
      </div>

      {(showGrpNav && products.length > 0) && (
        <GroupNav />
      )}

      {(subnav && !showGrpNav) && (
        <Subnav />
      )}

      <Bar />

      {(storeGendConflct && locate.currStores.length > 0) && (
        <div className={styles.gend_notapp}>
          Not filtering this collection by {storeName()}
        </div>
      )}

      <div className={styles.products} plp-prods="">
        {collPanel && (
          <div 
            ref={panelRef} 
            aria-hidden={'true'}
            className={styles.panel}
          >
            {(collPanel && collPanel === 'filters') && (
              <Filters />
            )}
            
            {(collPanel && collPanel === 'sort') && (
              <Sorts />
            )}
          </div>
        )}

        <button 
          type="button"
          className={[aria_btn, styles.bg].join(' ')} 
          aria-hidden={collPanel ? 'false' : 'true'} 
          onClick={(e)=> {panelClose()}} 
          aria-label={'Click to close panel'}
        />

        <div className={styles.tiles_wr}>
          {products.length > 0 && products.map((product_arr, index) => {
            return (
              <React.Fragment key={`tiles${index}`}>
                {is_block(product_arr) ? (
                  <CollBlocks blocks={product_arr.blocks} />
                ) : (
                  <div className={`${styles.tiles} ${styles[`t${scale}`]}`} ref={tilesRef}>
                    {(product_arr && product_arr?.length > 0) && product_arr.filter(x => !is_block(x)).map((product, index) => 
                      <React.Fragment key={tileKey(product, index)}>
                        {product.hasOwnProperty('products') ? (
                          <CollGroup groupData={product} index={index} tileIndxIds={tileIndxIds} />
                        ) :  (
                          <Tile 
                            product={product} 
                            tile_ind={index} 
                            is_plp={true} 
                            flats={flats} 
                            show={tileIndxIds.includes(product.mid)} 
                            handleQuick={handleQuick} 
                          />
                        )}
                      </React.Fragment>
                    )}
                  </div>
                )}
              </React.Fragment>
            )
          })}
          {ungroups?.length > 0 && (
            <div className={`${styles.tiles} ${styles[`t${scale}`]}`}>
              <div className={[gstyles.tile_group, styles.tile_group].join(' ')} tile-group="">
                <div className={gstyles.title}>Not in {currGroup} groups</div>
              </div>
              {ungroups.map((product,index) => 
                <Tile 
                  key={product.handle}
                  product={product} 
                  tile_ind={index} 
                  is_plp={true} 
                  flats={flats} 
                  show={tileIndxIds.includes(product.mid)} 
                  handleQuick={handleQuick} 
                />
              )}
            </div>
          )}
        </div>
      </div>

      {notBlank(seodesc) && (
        <SeoDescription
          title={title}
          description={seodesc}
        />
      )}
    </div>
  )
}

export default Plp
